import {
  Body1,
  Body2,
  Box,
  Button,
  Container,
  Display2,
  Flex,
  Grid,
  GridItem,
} from '@sainsburys-tech/fable'
import { AlertCircleFilled } from '@sainsburys-tech/icons'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import BannerHolder from '../../../common/components/Banners/BannerHolder'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { setBannerMessages } from '../../../common/Context/commonDispatch'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { checkStore, getUserStore } from '../../../utils/localStore'
import { getBatchItems, getPizzaAndIsbProducts } from '../../api/displayApi'
import { IBatchItemDisplay } from '../../types/IBatchItemDisplay'
import { IStackBanner } from '../../types/IStackBanner'
import BatchItemCard from './BatchItemCard'

const DisplayView = () => {
  const { dispatch, state } = useContext(Context)
  const navigate = useNavigate()
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [batchItems, setBatchItems] = useState<IBatchItemDisplay[]>([])
  const [isPizzaAndIsbProductsEnabled, setIsPizzaAndIsbProductsEnabled] =
    useState(false)

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.HotFood,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    const loadBatchItemsData = async () => {
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const [batchItemsData, isbProductsData, isStoreZeroErrorsEnabled] =
          await Promise.all([
            getBatchItems(apiClient, store.storeId, new Date()),
            getPizzaAndIsbProducts(apiClient, store.storeId),
            isFeatureEnabled(apiClient, Feature.StoreZeroErrors, 0),
          ])

        if (isStoreZeroErrorsEnabled) {
          const storeError = checkStore(store)
          if (storeError) {
            handleError(storeError)
          }
        }

        setBatchItems(batchItemsData)

        const isb = isbProductsData.filter((p) => p.isIsbProduct).length > 0
        setIsPizzaAndIsbProductsEnabled(isb)
      } catch (e) {
        handleError(ErrorMessage.BatchDisplayView.FailedToLoad, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadBatchItemsData()
  }, [apiClient, store, handleError, resetError])

  const removeBatchItem = (batchItemId: number) => {
    setBatchItems(batchItems.filter((x) => x.batchItemId !== batchItemId))
  }

  const refreshBatchItems = async () => {
    try {
      const batchItems = await getBatchItems(
        apiClient,
        store.storeId,
        new Date(),
      )
      setBatchItems(batchItems)
    } catch (e) {
      handleError(ErrorMessage.BatchDisplayView.FailedToLoad, e as Error)
    }
  }

  const saveBanners = (banners: IStackBanner[]) => {
    dispatch(setBannerMessages(banners))
  }

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      {!isLoading && (
        <Box padding='none' backgroundColor='primary'>
          <Header
            title={headerPlannerTitles.hotfood}
            navItems={headerNavigationItems}
            activeMenuType={Menu.Display}
          />

          <div>
            <Container className='c-common-main-view-content'>
              <BannerHolder
                banners={state.bannerMessages}
                setBanners={saveBanners}
              />
              {errorMessage && <ErrorRefresh message={errorMessage} />}

              {!isLoading && !errorMessage && (
                <>
                  <Grid
                    cols={12}
                    gap='lg'
                    className='hotfood-display-intro-grid'
                  >
                    <GridItem colSpan={6}>
                      <Display2>On display</Display2>
                      <Body1 style={{ marginTop: '25px' }}>
                        Keep track of products currently on display.
                      </Body1>
                    </GridItem>
                    {isPizzaAndIsbProductsEnabled && (
                      <GridItem colSpan={6}>
                        <Button
                          className='c-display-add-pizza-button'
                          onClick={() => navigate('/hotfoodpizza')}
                          variant='secondary'
                          testid='add-pizza-button'
                        >
                          Add pizza and ISB products
                        </Button>
                      </GridItem>
                    )}
                  </Grid>

                  {batchItems.length === 0 && (
                    <>
                      <Flex alignItems='center' direction='row' gap='sm'>
                        <AlertCircleFilled className='c-display-no-batch-icon' />
                        <Body2 className='c-display-no-batch-header-text'>
                          We have no products to display
                        </Body2>
                      </Flex>
                      <Body1 className='c-display-no-batch-text'>
                        You don't currently have any products on display.
                        Products will be shown here once temperature checks (and
                        display selections if applicable) have been completed.
                      </Body1>
                    </>
                  )}

                  <Grid cols={12} gap='lg' className='hotfood-display-grid'>
                    {batchItems
                      .sort(
                        (a: IBatchItemDisplay, b: IBatchItemDisplay) =>
                          new Date(a.toBeDisposedAt).getTime() -
                            new Date(b.toBeDisposedAt).getTime() ||
                          a.skuName.localeCompare(b.skuName),
                      )
                      .map((item, i) => (
                        <GridItem
                          key={i}
                          colSpan={{ default: 12, md: 6, lg: 4 }}
                        >
                          <BatchItemCard
                            batchItem={item}
                            removeBatchItem={() =>
                              removeBatchItem(item.batchItemId)
                            }
                            refreshBatchItems={() => refreshBatchItems()}
                          />
                        </GridItem>
                      ))}
                  </Grid>
                </>
              )}
            </Container>
          </div>
        </Box>
      )}
    </>
  )
}

export default DisplayView
